import type { MouseEvent } from "react";
import { WalletOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ActivePaymentMethod } from "../../../../../api/paymentMethods";
import { TablesEmptyState } from "../TablesEmptyState";
import type { BillClientDiscount } from "./BillTotalsBreakdown";
import { CashChangePanel } from "./CashChangePanel";
import { CloseTipColumn } from "./CloseTipColumn";
import { useViewport } from "../../../../../hooks/useViewport";
import { CLOSE_COLUMN_LABEL, CLOSE_COLUMN_TITLE } from "./closeLayout";
import { CloseMethodRow } from "./CloseMethodRow";
import { MixedPaymentPanel } from "./MixedPaymentPanel";
import type { BillCloseTip } from "./useBillCloseTip";
import { DepositPaymentSummary } from "./DepositPaymentSummary";
import type { CloseDeposit } from "./useCloseDeposit";
import { formatCents } from "../../../../../utils/money";

interface ClosePaymentBodyProps {
  closeTip: BillCloseTip;
  /** Режимът с бакшиш (`isTipLayout`) — иначе колоните са старите 38/62. */
  tipLayout: boolean;
  methods: ActivePaymentMethod[];
  selectedPaymentMethodId: number | null;
  /** Клик на активен метод — какво още става (трите клика на „В брой",
   *  ръчен избор) решава модалът. */
  onPickMethod: (methodId: number, event: MouseEvent) => void;
  /** Сумата за плащане като низ с два знака. */
  totalAmount: string;
  methodNameOf: (methodId: number) => string;
  cashChangeEnabled: boolean;
  cashReceived: string;
  onCashReceivedChange: (value: string) => void;
  clientDiscount: BillClientDiscount | null;
  mixedCashAmount: number;
  mixedCardAmount: number;
  onMixedCashChange: (cash: number) => void;
  onMixedCardChange: (card: number) => void;
  canPaymentCash: boolean;
  canPaymentCard: boolean;
  canOnAccount: boolean;
  cashOnlyMode: boolean;
  isNarrow: boolean;
  disabled: boolean;
  /** Закаченият към сметката депозит (или записаният при повторен опит) —
   *  само в „Закрий"; без него тялото е старото. */
  closeDeposit?: CloseDeposit;
}

/**
 * Тялото на модала за плащане — общо за „Закрий" и „Плати избраните" от
 * „Раздели", за да са 1:1: лявата колона (ресто/бакшиш/смесено/сума) и
 * методите вдясно. Модалът държи състоянието и заявката; тук е само изгледът.
 */
export function ClosePaymentBody({
  closeTip,
  tipLayout,
  methods,
  selectedPaymentMethodId,
  onPickMethod,
  totalAmount,
  methodNameOf,
  cashChangeEnabled,
  cashReceived,
  onCashReceivedChange,
  clientDiscount,
  mixedCashAmount,
  mixedCardAmount,
  onMixedCashChange,
  onMixedCardChange,
  canPaymentCash,
  canPaymentCard,
  canOnAccount,
  cashOnlyMode,
  isNarrow,
  disabled,
  closeDeposit,
}: ClosePaymentBodyProps) {
  const { t, i18n } = useTranslation();
  const { isTablet } = useViewport();
  const selectedMethod = methods.find((m) => m.id === selectedPaymentMethodId);
  // Със закачен депозит лявата колона е сметката „сметка − депозит = за
  // доплащане" и какво ще стане, а методите са само за остатъка — „Смесено"
  // разпределя него (полетата са под сметката). Без интернет депозитът не се
  // приспада — лентата за това е под хедъра на модала (`DepositOfflineStrip`),
  // тялото е обичайното.
  const chosenDeposit = closeDeposit?.deposit ?? null;
  const depositCoverage = closeDeposit?.coverage ?? null;
  const depositCoversAll = !!depositCoverage && depositCoverage.remainderCents === 0;
  // Повторният опит показва записания метод само за четене — без полета.
  const depositMixed =
    !!chosenDeposit && !!depositCoverage && !closeDeposit?.recorded && selectedPaymentMethodId === 3;
  const mixedTotalAmount =
    chosenDeposit && depositCoverage ? formatCents(depositCoverage.remainderCents) : totalAmount;
  // С депозит колоната на бакшиша я няма (депозитът е без бакшиш) — полетата
  // на остатъка са под колоните и при включен бакшиш.
  const narrowMixedBelow =
    isNarrow && (chosenDeposit ? depositMixed : !tipLayout && selectedPaymentMethodId === 3);

  // Депозитът покрива всичко — метод не се избира, затова колоната с
  // методите я няма: модалът е само сметката и какво ще стане.
  if (chosenDeposit && depositCoverage && depositCoversAll) {
    return (
      <div className={isNarrow ? "px-4 py-3.5" : "bg-pos-canvas px-6 py-5"}>
        <DepositPaymentSummary
          deposit={chosenDeposit}
          coverage={depositCoverage}
          totalAmount={totalAmount}
          remainderMethodName={null}
          recorded={!!closeDeposit?.recorded}
          blocksZeroBill={!!closeDeposit?.blocksZeroBill}
          isNarrow={isNarrow}
        />
      </div>
    );
  }

  return (
    <div
      className={
        isNarrow
          ? "flex flex-col gap-4 px-4 py-3.5"
          : // Широк екран: две тонирани колони, допрени на вертикален
            // разделител — паддингът е ВЪТРЕ в колоните, не на обвивката
            "flex items-stretch"
      }
    >
      {/* Лява колона: на телефон е СТАРИЯТ изглед 1:1 (метод + сума,
          без ресто помощник); панелите по метод са само за широк екран */}
      <div
        className={
          isNarrow
            ? "flex min-w-0 flex-1 flex-col"
            : `flex min-w-0 flex-col bg-pos-canvas px-5 py-5 ${
                // С бакшиш (и със закачен депозит — двете суми една до друга)
                // лявата е 55 %, методите вдясно — 45 %
                tipLayout || chosenDeposit ? "w-[55%]" : "w-[38%]"
              }`
        }
      >
        {chosenDeposit && depositCoverage ? (
          <>
            <DepositPaymentSummary
              deposit={chosenDeposit}
              coverage={depositCoverage}
              totalAmount={totalAmount}
              remainderMethodName={selectedMethod ? methodNameOf(selectedMethod.id) : null}
              recorded={!!closeDeposit?.recorded}
              blocksZeroBill={!!closeDeposit?.blocksZeroBill}
              isNarrow={isNarrow}
            />
            {/* „Смесено" — разпределението на остатъка, под сметката (на
                телефон — под колоните, както без депозит) */}
            {depositMixed && !isNarrow && (
              <div className="mt-5">
                <MixedPaymentPanel
                  large
                  totalAmount={mixedTotalAmount}
                  cashAmount={mixedCashAmount}
                  cardAmount={mixedCardAmount}
                  onCashChange={onMixedCashChange}
                  onCardChange={onMixedCardChange}
                  disabled={disabled}
                  showTotal={false}
                />
              </div>
            )}
          </>
        ) : tipLayout ? (
          <CloseTipColumn
            closeTip={closeTip}
            billMethodId={selectedPaymentMethodId}
            totalAmount={totalAmount}
            methodNameOf={methodNameOf}
            cashChangeEnabled={cashChangeEnabled}
            cashReceived={cashReceived}
            onCashReceivedChange={onCashReceivedChange}
            mixedCashAmount={mixedCashAmount}
            mixedCardAmount={mixedCardAmount}
            onMixedCashChange={onMixedCashChange}
            onMixedCardChange={onMixedCardChange}
            isNarrow={isNarrow}
            disabled={disabled}
          />
        ) : isNarrow ? (
          <>
            <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted">
              {t("closeTable.paymentMethod")}
            </div>
            <div className="truncate text-base font-bold tracking-[-0.3px] text-pos-ink">
              {selectedMethod
                ? i18n.language === "en"
                  ? selectedMethod.name_en
                  : selectedMethod.name_bg
                : t("closeTable.notSelected")}
            </div>
            <div className="mt-1.5 font-pos-mono text-[32px] font-bold leading-none tracking-[-1.2px] text-pos-accent [font-variant-numeric:tabular-nums]">
              {parseFloat(totalAmount).toFixed(2)}
            </div>
          </>
        ) : selectedPaymentMethodId === 1 && cashChangeEnabled ? (
          /* „В брой" — помощникът за ресто (само визуален) */
          <CashChangePanel
            totalAmount={totalAmount}
            received={cashReceived}
            onReceivedChange={onCashReceivedChange}
            disabled={disabled}
            discount={clientDiscount}
          />
        ) : selectedPaymentMethodId === 3 ? (
          /* „Смесено" — разпределението, В брой и С карта едно под друго */
          <MixedPaymentPanel
            large
            discount={clientDiscount}
            totalAmount={totalAmount}
            cashAmount={mixedCashAmount}
            cardAmount={mixedCardAmount}
            onCashChange={onMixedCashChange}
            onCardChange={onMixedCardChange}
            disabled={disabled}
          />
        ) : (
          <>
            <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted">
              {t("closeTable.totalToPay")}
            </div>
            <div
              className={`font-pos-mono font-bold leading-none tracking-[-1.2px] text-pos-accent [font-variant-numeric:tabular-nums] ${
                isNarrow ? "text-[32px]" : "text-[40px]"
              }`}
            >
              {parseFloat(totalAmount).toFixed(2)}{" "}
              <span className="text-base text-pos-muted">EUR</span>
            </div>
            {/* Подсказка в дъното на колоната — само при „С карта" */}
            {selectedPaymentMethodId === 2 && (
              <div className="mt-auto pt-6 text-[13px] font-medium text-pos-soft2">
                {t("closeTable.cardTerminalHint")}
              </div>
            )}
          </>
        )}
      </div>

      {/* Дясна колона: методите на плащане — по-топъл тон от лявата */}
      <div
        className={
          isNarrow
            ? "min-w-0 flex-1"
            : "min-w-0 flex-1 border-l border-pos-line2 bg-pos-marked px-5 py-5"
        }
      >
        <div
          className={
            tipLayout
              ? // Таблет с двете колони — 13px, като „Общо за плащане:" там.
                `${CLOSE_COLUMN_TITLE} ${isTablet && !isNarrow ? "text-[13px]" : "text-[14px]"}`
              : CLOSE_COLUMN_LABEL
          }
        >
          {/* Със закачен депозит методът е само за остатъка — заглавието казва колко */}
          {chosenDeposit && depositCoverage
            ? t("closeTable.deposit.remainderMethodTitle", {
                amount: formatCents(depositCoverage.remainderCents),
              })
            : t("closeTable.selectMethod")}
        </div>
        {methods.length === 0 ? (
          <div>
            <TablesEmptyState
              icon={<WalletOutlined />}
              title={t("closeTable.noMethodsTitle")}
              description={t("closeTable.noMethodsDescription")}
            />
          </div>
        ) : (
          <div
            className={`flex flex-col ${isNarrow ? "gap-1.5" : "gap-2.5"}`}
          >
            {/* „По сметка" (4) е СКРИТ без клиент с правото */}
            {methods
              .filter((m) => m.id !== 4 || canOnAccount)
              .map((method) => {
                const isSelected = selectedPaymentMethodId === method.id;
                // Повторен опит с депозит: методът е записаният — нищо не се избира.
                const isDisabled =
                  !!closeDeposit?.recorded ||
                  (method.id === 1 && !canPaymentCash) ||
                  (method.id === 2 && !canPaymentCard) ||
                  (method.id === 3 &&
                    (!canPaymentCash || !canPaymentCard));
                const isHidden = cashOnlyMode && method.id !== 1;

                return (
                  <CloseMethodRow
                    key={method.id}
                    label={i18n.language === "en" ? method.name_en : method.name_bg}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    isHidden={isHidden}
                    isNarrow={isNarrow}
                    // С бакшиш — точно колкото кутийките вляво (банкноти, проценти)
                    boxed={tipLayout}
                    onClick={(event) => onPickMethod(method.id, event)}
                  />
                );
              })}
          </div>
        )}
      </div>

      {/* Телефон без бакшиш: смесеното разпределение е ПОД колоните, както
          в стария изглед (без блока „Общо"); с бакшиш е `MixedTipPanel` горе;
          с депозит — разпределя остатъка */}
      {narrowMixedBelow && (
        <MixedPaymentPanel
          totalAmount={mixedTotalAmount}
          cashAmount={mixedCashAmount}
          cardAmount={mixedCardAmount}
          onCashChange={onMixedCashChange}
          onCardChange={onMixedCardChange}
          disabled={disabled}
          showTotal={false}
        />
      )}
    </div>
  );
}
